import { computed, type ComputedRef, type Ref } from 'vue';

import { appT } from '~/composables/useAppI18n';

type SelectOption = { label: string; value: string; [key: string]: unknown };

type GoalTypeFilter = 'all' | 'kpi' | 'okr' | string;

/** store·비동기 handler에서 KPI i18n t() 사용 */
export const kpiT = (key: string, params?: Record<string, unknown>) => {
  const fullKey = key.startsWith('performance.kpi.') ? key : `performance.kpi.${key}`;
  return appT(fullKey, params);
};
export const getKpiTypeLabel = (kpiTypeCd: string) => {
  if (kpiTypeCd === '01') return appT('performance.kpi.goalTable.quantitative');
  if (kpiTypeCd === '02') return appT('performance.kpi.goalTable.qualitative');
  return '';
};

/** 목표유형 필터 옵션 라벨 */
export const getGoalTypeLabel = (value: string) => {
  const key = `performance.kpi.goalType.${value}`;
  const translated = appT(key);
  return translated !== key ? translated : value;
};

/** 빈 목록 메시지 */
export const getGoalEmptyMessage = (goalTypeFilter: GoalTypeFilter) => {
  if (goalTypeFilter === 'kpi') return appT('performance.kpi.goalTable.emptyKpi');
  if (goalTypeFilter === 'okr') return appT('performance.kpi.goalTable.emptyOkr');
  return appT('performance.kpi.goalTable.emptyGoal');
};

/** '전체' value('') 옵션 라벨 치환 */
export const localizeAllSelectOptions = (options: SelectOption[]) =>
  options.map(option => (option.value === '' ? { ...option, label: appT('common.action.all') } : option));

/** 분기명(서버 한국어) → locale 라벨 (예: 2분기 → Q2) */
export const getCycleLabel = (cycleNm?: string | null) => {
  if (!cycleNm) return '';

  const trimmed = cycleNm.trim();
  const quarterMatch = trimmed.match(/(\d)분기$/);
  if (quarterMatch) {
    return appT(`task.reports.cycle.quarter${quarterMatch[1]}`);
  }

  const cycleLabelKeys: Record<string, string> = {
    상반기: 'task.reports.cycle.firstHalf',
    하반기: 'task.reports.cycle.secondHalf',
  };

  const key = cycleLabelKeys[trimmed];
  if (key) return appT(key);

  return cycleNm;
};

/** 실적월(1~12) 표시 — ko: 5월, en: May */
export const getActualMonthLabel = (actualMon: string | number, localeCode?: string) => {
  const month = parseInt(String(actualMon), 10);
  if (!Number.isFinite(month) || month < 1 || month > 12) {
    return String(actualMon ?? '');
  }

  let loc = localeCode;
  if (!loc) {
    try {
      loc = (useNuxtApp().$i18n as { locale: { value: string } }).locale.value;
    } catch {
      loc = 'ko';
    }
  }

  if (loc.startsWith('ko')) {
    return kpiT('actualTab.monthLabel', { month });
  }

  const tag = loc.startsWith('en') ? 'en-US' : loc;
  return new Intl.DateTimeFormat(tag, { month: 'long' }).format(new Date(2000, month - 1, 1));
};

/** Vue 컴포넌트 — locale 변경 시 라벨 재계산 */
export const useKpiI18n = () => {
  const { locale, t } = useI18n();

  const kpiTypeLabel = (kpiTypeCd: string) => {
    locale.value;
    return getKpiTypeLabel(kpiTypeCd);
  };

  const goalTypeLabel = (value: string) => {
    locale.value;
    return getGoalTypeLabel(value);
  };

  const goalEmptyMessage = (goalTypeFilter: GoalTypeFilter) => {
    locale.value;
    return getGoalEmptyMessage(goalTypeFilter);
  };

  const localizeGoalTypeOptions = (options: Ref<SelectOption[]> | ComputedRef<SelectOption[]> | SelectOption[]) =>
    computed(() => {
      locale.value;
      const list = 'value' in options ? options.value : options;
      return list.map(option => ({
        ...option,
        label: goalTypeLabel(option.value),
      }));
    });

  const localizeAllOptions = (options: Ref<SelectOption[]> | ComputedRef<SelectOption[]>) =>
    computed(() => {
      locale.value;
      return localizeAllSelectOptions(options.value);
    });

  const localizeCycleOptions = (options: Ref<SelectOption[]> | ComputedRef<SelectOption[]>) =>
    computed(() => {
      locale.value;
      return options.value.map(option =>
        option.value === '' ? { ...option, label: t('common.action.all') } : { ...option, label: getCycleLabel(option.label) || option.label },
      );
    });

  const cycleLabel = (cycleNm?: string | null) => {
    locale.value;
    return getCycleLabel(cycleNm);
  };

  const actualMonthLabel = (actualMon: string | number) => {
    locale.value;
    return getActualMonthLabel(actualMon, locale.value);
  };

  return {
    t,
    kpiT,
    locale,
    kpiTypeLabel,
    goalTypeLabel,
    goalEmptyMessage,
    localizeGoalTypeOptions,
    localizeAllOptions,
    localizeCycleOptions,
    cycleLabel,
    actualMonthLabel,
  };
};
